import type { ComponentType, ReactNode } from 'react'

import type { DashboardCardKey } from '../../../../../../common/dashboard-cards.js'
import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import type {
    BalanceTimeCardPayload,
    MyTasksPayload,
    TeamWorkloadPayload,
    WorktimeCardPayload,
} from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import type { IconProps } from '../../../../../components/icon/icon.js'
import {
    Activity,
    BarChart3,
    Briefcase,
    CalendarHeart,
    CheckSquare,
    Donut,
    MapPin,
    PartyPopper,
    Receipt,
    Scale,
    Sun,
    Trophy,
    UserCheck,
    Users,
} from '../../../../../components/icon/solar-icons.js'
import { BalanceTimeCard } from './balancetime-card.js'
import { EatDebtCard } from './eat-debt-card.js'
import { HeadcountCard } from './headcount-card.js'
import { LeaderboardCard } from './leaderboard-card.js'
import { LeaveBalanceCard } from './leave-balance-card.js'
import { Life360Card } from './life360-card.js'
import { MyTasksCard } from './my-tasks-card.js'
import { MyTenureCard } from './my-tenure-card.js'
import { Next7DaysEventsCard } from './next-7-days-events-card.js'
import { NextEventCard } from './next-event-card.js'
import { NextInternalEventCard } from './next-internal-event-card.js'
import { NextJoinerLeaverCard } from './next-joiner-leaver-card.js'
import { TeamNormCard } from './team-norm-card.js'
import { TeamWorkloadCard } from './team-workload-card.js'
import { TenureCard } from './tenure-card.js'
import { WorktimeCard } from './worktime-card.js'

export type CardComponentProps = { data: unknown; lang: Language; isMobile: boolean }

export const CARD_COMPONENTS: Record<DashboardCardKey, ComponentType<CardComponentProps>> = {
    headcount: HeadcountCard as ComponentType<CardComponentProps>,
    leave_balance: LeaveBalanceCard as ComponentType<CardComponentProps>,
    my_tasks: MyTasksCard as ComponentType<CardComponentProps>,
    my_tenure: MyTenureCard as ComponentType<CardComponentProps>,
    balancetime: BalanceTimeCard as ComponentType<CardComponentProps>,
    tenure: TenureCard as ComponentType<CardComponentProps>,
    next_7_days_events: Next7DaysEventsCard as ComponentType<CardComponentProps>,
    team_workload: TeamWorkloadCard as ComponentType<CardComponentProps>,
    team_norm: TeamNormCard as ComponentType<CardComponentProps>,
    worktime: WorktimeCard as ComponentType<CardComponentProps>,
    eat_debt: EatDebtCard as ComponentType<CardComponentProps>,
    leaderboard: LeaderboardCard as ComponentType<CardComponentProps>,
    next_internal_event: NextInternalEventCard as ComponentType<CardComponentProps>,
    next_joiner_leaver: NextJoinerLeaverCard as ComponentType<CardComponentProps>,
    next_event: NextEventCard as ComponentType<CardComponentProps>,
    life360: Life360Card as ComponentType<CardComponentProps>,
}

const ICON_SIZE = 18

const INTEGRATIONS_PATH = '/me/settings'

export interface CardAction {
    label: string
    path: string
    /** Optional trailing icon (e.g. an external-link arrow). */
    icon?: ComponentType<IconProps>
    /** Open `path` in a new tab instead of in-app navigation. */
    external?: boolean
}

// Trailing arrow for CTAs that open in a new tab — forced Linear so it stays a
// simple outline even under the dashboard's duotone provider.

interface CardVisuals {
    icon: ReactNode
    /** Header CTA, rendered by CardFrame as a right-aligned secondary button. */
    action?: (data: unknown, lang: Language) => CardAction | null
}

export const CARD_VISUALS: Record<DashboardCardKey, CardVisuals> = {
    headcount: { icon: <Users size={ICON_SIZE} strokeWidth={2} /> },
    leave_balance: {
        icon: <Sun size={ICON_SIZE} weight="BoldDuotone" />,
        action: (_data, lang) => ({
            label: t.portal.dashboard.cards.leave_balance.viewLink(lang),
            path: '/me/time-away',
        }),
    },
    my_tasks: {
        icon: <CheckSquare size={ICON_SIZE} strokeWidth={2} />,
        // "View all" opens the full Notion tasks page; shown only when the
        // user actually has tasks to view.
        action: (data, lang) => {
            const d = data as MyTasksPayload
            return d.status === 'connected' && d.groups.length > 0
                ? { label: t.portal.dashboard.cards.my_tasks.viewAll(lang), path: '/me/tasks' }
                : null
        },
    },
    balancetime: {
        icon: <Scale size={ICON_SIZE} strokeWidth={2} />,
        // The "open" CTA lives at the bottom of the card body; only the
        // "add token" prompt stays in the header (unavailable state).
        action: (data, lang) =>
            (data as BalanceTimeCardPayload).status === 'unavailable'
                ? {
                      label: t.portal.dashboard.cards.my_tasks.addToken(lang),
                      path: INTEGRATIONS_PATH,
                  }
                : null,
    },
    my_tenure: { icon: <Briefcase size={ICON_SIZE} strokeWidth={2} /> },
    tenure: { icon: <Briefcase size={ICON_SIZE} strokeWidth={2} /> },
    next_7_days_events: {
        icon: <Donut size={ICON_SIZE} weight="BoldDuotone" />,
    },
    team_workload: {
        icon: <Activity size={ICON_SIZE} strokeWidth={2} />,
        // "View all" opens the calendar pre-filtered to the card's team.
        action: (data, lang) => {
            const d = data as TeamWorkloadPayload
            return {
                label: t.portal.dashboard.cards.team_workload.viewLink(lang),
                path:
                    d.status === 'ok' && d.teamId !== null
                        ? `/calendar?teamId=${d.teamId}`
                        : '/calendar',
            }
        },
    },
    team_norm: {
        icon: <BarChart3 size={ICON_SIZE} strokeWidth={2} />,
    },
    worktime: {
        icon: <BarChart3 size={ICON_SIZE} strokeWidth={2} />,
        // No "view projects" CTA when the card is empty (no projects entered).
        action: (data, lang) =>
            (data as WorktimeCardPayload).projectCount === 0
                ? null
                : {
                      label: t.portal.dashboard.cards.worktime.viewLink(lang),
                      path: '/me/worktime',
                  },
    },
    eat_debt: { icon: <Receipt size={ICON_SIZE} strokeWidth={2} /> },
    leaderboard: {
        icon: <Trophy size={ICON_SIZE} strokeWidth={2} />,
        action: (_data, lang) => ({
            label: t.portal.dashboard.cards.leaderboard.viewLink(lang),
            path: '/badges',
        }),
    },
    next_internal_event: { icon: <PartyPopper size={ICON_SIZE} strokeWidth={2} /> },
    next_joiner_leaver: { icon: <UserCheck size={ICON_SIZE} strokeWidth={2} /> },
    next_event: {
        icon: <CalendarHeart size={ICON_SIZE} strokeWidth={2} />,
        action: (_data, lang) => ({
            label: t.portal.dashboard.cards.next_event.viewLink(lang),
            path: '/events',
        }),
    },
    life360: { icon: <MapPin size={ICON_SIZE} strokeWidth={2} /> },
}

export { CARD_IS_ACTIVE, isCardActive } from './card-visibility.js'
